import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Form, Button, message, Space, Tag, Alert } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import { useTheme } from '../contexts/ThemeContext';
import { promptsApi, PromptsResponse } from '../services/prompts';

const PromptManagement: React.FC = () => {
  const [prompts, setPrompts] = useState<PromptsResponse>({});
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState<string>('gen_document');
  const { theme } = useTheme();

  const loadPrompts = useCallback(async () => {
    try {
      const data = await promptsApi.getAll();
      setPrompts(data);
      if (data[activeKey]) {
        form.setFieldsValue({ template: data[activeKey].template });
      }
    } catch (error) {
      message.error('加载提示词失败');
    }
  }, [activeKey, form]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const handleTabChange = (key: string) => {
    setActiveKey(key);
    if (prompts[key]) {
      form.setFieldsValue({ template: prompts[key].template });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await promptsApi.update(activeKey, values);
      message.success('保存成功');
      loadPrompts();
    } catch (error) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const promptTypes = [
    { key: 'gen_document', label: '文档生成' },
    { key: 'gen_soul', label: '数字人人设' },
    { key: 'gen_skill', label: '技能文档' },
    { key: 'gen_tool', label: '工具文档' },
    { key: 'gen_application', label: '应用介绍' },
  ];

  const tabItems = promptTypes.map((type) => ({
    key: type.key,
    label: type.label,
    children: prompts[type.key] ? (
      <div>
        <Alert
          message={prompts[type.key].description}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>变量列表：</div>
          <div>
            {prompts[type.key].variables.map((v) => (
              <Tag key={v} color="blue" style={{ marginBottom: 4 }}>
                {`{${v}}`}
              </Tag>
            ))}
          </div>
          <Alert
            message="系统占位符说明"
            description={
              <div style={{ fontSize: 12, lineHeight: '1.8' }}>
                <div>• <code>$ROOT</code> - 后端根目录</div>
                <div>• <code>$HOME</code> - 数字人 home 目录</div>
                <div>• <code>$EXPERT</code> - 专家列表</div>
                <div>• <code>$TAG:dict_code:format</code> - 字典标签</div>
                <div>• <code>{`{identifier}`}</code> - 数字人标识符</div>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginTop: 12, marginBottom: 16 }}
          />
        </div>

        <Form form={form} layout="vertical">
          <Form.Item
            label="提示词模板"
            name="template"
            rules={[{ required: true, message: '请输入提示词模板' }]}
          >
            <Editor
              height="500px"
              language="markdown"
              theme={theme === 'glass' ? 'vs-dark' : 'light'}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                wordWrap: 'on',
              }}
            />
          </Form.Item>
        </Form>

        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            保存
          </Button>
          <Button onClick={() => loadPrompts()}>重置</Button>
        </Space>
      </div>
    ) : null,
  }));

  return (
    <div className="apple-page">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="apple-page-title" style={{ margin: 0 }}>提示词管理</h1>
      </div>
      <Tabs activeKey={activeKey} onChange={handleTabChange} items={tabItems} />
    </div>
  );
};

export default PromptManagement;
